import React from 'react';
import { Callout, DocHeading, DocTable, Shot } from './components';

function PageFarmStats({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Farm Stats</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        While the bot runs, it counts what your character gains: gold, Andermant, Draken and five
        farming currencies. The same numbers appear on this site under Dashboard → Farming. There
        is nothing to switch on &mdash; counting starts by itself. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Stats</strong> tab.
      </p>

      <DocHeading>The FARM SESSION Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<><strong className="text-white">Auto restart</strong> <span className="text-emerald-400">watched - next in 1:23:44</span></>, <>What the loader&rsquo;s Auto Restart does with this game: <em>watched - next in &hellip;</em>, <em>restarting now</em>, <em>off in the loader</em>, <em>not watched (unticked in the loader)</em> or <em>not watched by the loader</em>. See <button onClick={() => navigate('auto-restart')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Restart</button>.</>],
          [<><strong className="text-white">Sharing</strong> <span className="text-emerald-400">active</span></>, 'A session is running and its numbers are handed to the loader, which reports them to your account.'],
          [<><strong className="text-white">Sharing</strong> waiting for a character</>, 'The bot is loaded, but no character is in the game yet (for example at the character selection).'],
          [<><strong className="text-white">Sharing</strong> <span className="text-amber-300">not available</span></>, 'The hand-over to the loader could not be set up, so nothing reaches your account. Farming and the counters on this tab still work.'],
          [<strong className="text-white">Server</strong>, 'The game server number of this session. The Farming tab can filter the statistics by it.'],
          [<strong className="text-white">Started</strong>, 'When this session began, in your local time.'],
          [<strong className="text-white">Running</strong>, 'How long the session has been going.'],
          [<strong className="text-white">Updates</strong>, 'How many times the numbers have been handed over. It rises every few seconds while the session runs.'],
          [<><strong className="text-white">Counting</strong> paused - loading</>, 'Shown during a loading screen, while the bot confirms which character is in the game. Nothing is lost: counting carries on from the same starting line.'],
          [<strong className="text-white">Session id</strong>, 'The identifier of this session. Quote it if you ask support about a specific run.'],
        ]}
      />
      <Callout type="info">
        <strong>One session belongs to one character.</strong> It starts when a character is in the
        game and ends at the character selection. Whoever you log in with next &mdash; even the same
        character &mdash; gets a new session with a new id, so two characters are never counted
        together. Changing maps with the same character keeps the same session.
      </Callout>

      <DocHeading>The THIS SESSION Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        What your character gained since the session started. These are counted:
      </p>
      <DocTable
        headers={['Currency', 'How quickly it updates']}
        rows={[
          ['Gold', 'Within a couple of seconds'],
          ['Andermant', 'Within a couple of seconds'],
          ['Draken', 'Within a couple of seconds'],
          ['Fragment of Infernal Passage', 'About every 15 seconds'],
          ['Shiny Dust', 'About every 15 seconds'],
          ['Rune Dust', 'About every 15 seconds'],
          ['Realm Fragment', 'Within a couple of seconds'],
          ['Materi Fragment', 'Within a couple of seconds'],
        ]}
      />
      <DocTable
        headers={['A row that reads', 'Means']}
        rows={[
          [<span className="text-emerald-400">+41g 46s 88c</span>, 'Gold gained. If you also spent gold, a second, orange line with a minus sign shows how much.'],
          [<span className="text-emerald-400">+174</span>, 'Gained, nothing spent.'],
          [<span className="text-white">+20&nbsp;&nbsp;(-5)</span>, 'Gained 20 and spent 5 during the session.'],
          [<span className="text-gray-500">-</span>, 'Measured, but nothing has changed yet.'],
          ['No row at all', 'Not read yet. A currency your character has never had may not show up at all. Not measured is not the same as zero, so it is left out instead of shown as 0.'],
        ]}
      />
      <DocTable
        headers={['Text under the list', 'What it means']}
        rows={[
          [<strong className="text-white">Gained since this session started.</strong>, 'The session is running.'],
          [<strong className="text-white">The last session - closed.</strong>, 'You went back to the character selection. The numbers of the closed session stay visible until the next character enters.'],
          [<strong className="text-white">Waiting for the first reading.</strong>, 'The starting balances are taken as soon as the character’s data has arrived, usually within seconds of logging in.'],
          [<strong className="text-white">No character in the game.</strong>, 'A new session starts when one enters.'],
        ]}
      />

      <Callout type="info">
        <strong>How it counts</strong> (also behind the small round <strong>i</strong> marker on the card).
        Balances are sampled and compared: a rise counts as earned, a drop as spent. So it does not
        matter how you got something &mdash; loot, quest, chest or trade. What your character
        already owned when the session started is the starting line, not a gain. Every report
        carries the running total, so a lost report costs nothing, and if the game crashes, the
        loader still closes the session with the last numbers it read.
      </Callout>

      <Shot
        name="farm-stats-01-stats-tab.png"
        tab="FARM › Stats"
        caption="A session 2 h 14 min in: watched by Auto Restart, sharing active; gold gained and spent, and the farming currencies. A dash means nothing has been measured for that currency yet."
        shoot="menushot (stage session + share + loaderlink): a FARM › Stats lap — FARM SESSION (Auto restart watched - next in 1:23:44, Sharing active, Server 815, Started 19:42, Running 2h 14m, Updates 268, Session id); THIS SESSION (Gold +18452g 37s 00c és narancs -250g, Andermant -, Draken +1240, a porok és töredékek). Karakternév nincs a képen."
      />

      <DocHeading>Why a Number Looks Off</DocHeading>
      <DocTable
        headers={['What you see', 'Why']}
        rows={[
          ['Something earned before the bot started is missing', 'Counting starts with the session. What the character already had is the starting line.'],
          ['Dust or fragments put into the bank show up as spent', 'The three item-type currencies (Fragment of Infernal Passage, Shiny Dust, Rune Dust) are counted from what the character carries. Moving them to the bank looks like a drop.'],
          ['I earned dust and melted it right away, but only the difference shows', 'Between two readings the ups and downs cancel out. The total at the end is still right.'],
          ['The dust rows lag behind the others', 'They are read about every 15 seconds. Give it a moment.'],
          ['The numbers started from zero after I switched characters', 'A new character is a new session, by design. The previous one is closed with its final numbers.'],
          ['A character with no gold at all shows no Gold, Andermant, Draken, Realm Fragment or Materi Fragment row yet', 'The starting line for these is taken once the character’s balances have arrived, and the sign for that is gold. They start counting with the first gold. The three item-type rows (Fragment of Infernal Passage, Shiny Dust, Rune Dust) do not wait for gold.'],
          ['The Farming tab is behind this tab', 'The loader reports roughly once a minute, so the website can be a minute or so behind.'],
        ]}
      />
    </div>
  );
}

export default PageFarmStats;
